/**
 * The playback rules, matched to the mobile replay so the same hunt reads the
 * same way on both. These are the numbers a hunting-trial judge works with, so
 * they are not free to drift between platforms.
 */

/** Multipliers of real time, in picker order. Playback always opens at 1×. */
export const REPLAY_SPEEDS = [1, 2, 10, 20, 30, 50, 100] as const

export type ReplaySpeed = (typeof REPLAY_SPEEDS)[number]

export const DEFAULT_REPLAY_SPEED: ReplaySpeed = 1

/**
 * How far one press of a skip button moves the cursor, in milliseconds.
 *
 * The step the viewer picks stays put until they pick another — it never
 * escalates with press rate. Judges have to decide whether a pause fell above or
 * below a fixed threshold (15 s for bird-barking, 2 min for elk, 5 min for the
 * hound classes), and a step that grows on the second press jumps straight over
 * those boundaries. Holding it still is also what makes an overshoot reversible
 * by pressing the same number of times back.
 *
 * The range is set by what the rules ask a judge to resolve: 15 s at the bottom
 * because bird-barking trials judge bark time to 15 s, 60 min at the top because
 * a trial recording can run to seven hours.
 */
export const REPLAY_SKIP_STEPS_MS = [
  15_000,
  30_000,
  60_000,
  5 * 60_000,
  10 * 60_000,
  30 * 60_000,
  60 * 60_000,
] as const

export type ReplaySkipStepMs = (typeof REPLAY_SKIP_STEPS_MS)[number]

/**
 * One minute, because whole-minute resolution is what most trial types tabulate
 * on, so the common case stays one press away.
 */
export const DEFAULT_REPLAY_SKIP_STEP_MS: ReplaySkipStepMs = 60_000

/**
 * How long a transient event (a bark) stays on screen, as a fraction of the whole
 * hunt.
 *
 * Scaling to hunt duration rather than wall time is what keeps an event readable
 * at 100×: a fixed two-second window would flash past within a single frame's
 * worth of hunt time, so the indicator would be invisible exactly when someone
 * is scanning quickly for it.
 */
const TRANSIENT_WINDOW_FRACTION = 0.045

/**
 * Floor and ceiling on that window, in hunt-time milliseconds.
 *
 * The floor keeps a short hunt's events legible. The ceiling matters more than it
 * looks: at 100x a window is on screen for a hundredth of its hunt duration, so
 * anything much under a minute of hunt time flashes past inside a single frame —
 * invisible exactly when someone is scanning fast for it. Ten minutes is the other
 * failure, where every bark stays lit long enough to read as one unbroken bay.
 */
const MIN_TRANSIENT_WINDOW_MS = 45_000
const MAX_TRANSIENT_WINDOW_MS = 6 * 60_000

export function transientWindowMs(huntDurationMs: number): number {
  const scaled = huntDurationMs * TRANSIENT_WINDOW_FRACTION
  return Math.min(MAX_TRANSIENT_WINDOW_MS, Math.max(MIN_TRANSIENT_WINDOW_MS, scaled))
}

/**
 * How long a chat message stays on screen, as a span of HUNT time.
 *
 * Deliberately not built the way `transientWindowMs` above is. A bark is a
 * stretch of hunt time that really did last that long, so scaling it to the
 * hunt is honest. A message is an instant that needs READING time, and how long
 * a sentence takes to read has nothing to do with how long the hunt was — a
 * WeHunt hunt runs a full day and sometimes several, so a fraction-of-the-hunt
 * window gives every message on every long hunt the same ceiling whether it
 * says "Ja" or three sentences.
 *
 * Two inputs instead: the message's own length, and the playback speed.
 *
 * The character rate is borrowed rather than invented. Subtitling solved this
 * exact problem — text that must be read inside a fixed window while the
 * viewer's attention is on the picture — and puts adult reading speed at around
 * 17 characters per second, with a floor under a second and a ceiling near
 * seven. {@link MESSAGE_MS_PER_CHARACTER} is 18 characters a second.
 * {@link MESSAGE_BASE_MS} is the one addition: a subtitle appears where the eye
 * already is, while a bubble appears somewhere on a map and has to be found.
 *
 * Multiplying by speed is what makes the result readable at every speed. The
 * window is measured against the hunt-time cursor, which advances `speed` times
 * faster than the wall clock, so `readingTime * speed` of hunt time is exactly
 * `readingTime` of real time on screen. The same message is equally readable at
 * 1x and at 100x.
 */
const MESSAGE_BASE_MS = 900
const MESSAGE_MS_PER_CHARACTER = 55
const MIN_MESSAGE_READING_MS = 2_500
const MAX_MESSAGE_READING_MS = 8_000

/**
 * The shortest span of HUNT time a message may occupy, whatever the speed.
 *
 * This one is for scrubbing, not for reading. Without it, at 1x a message
 * occupies about three seconds of a hunt that runs for hours, so dragging the
 * scrubber would essentially never land on one — and messages are meant to show
 * while scrubbing, not only while playing. It only binds below roughly 10x,
 * where playback is slow enough that a bubble lingering costs nothing.
 */
const MIN_MESSAGE_WINDOW_MS = 30_000

/**
 * How long the message itself takes to read, in WALL-CLOCK milliseconds.
 *
 * Counted in UTF-16 units rather than code points: the difference is one unit
 * per emoji, which only ever lengthens the window slightly, and the cheaper
 * count runs once per message per frame.
 */
export function messageReadingTimeMs(text: string): number {
  const scaled = MESSAGE_BASE_MS + MESSAGE_MS_PER_CHARACTER * text.length
  return Math.min(MAX_MESSAGE_READING_MS, Math.max(MIN_MESSAGE_READING_MS, scaled))
}

/** The span of HUNT time a message is visible for at `speed`. */
export function messageWindowMs(text: string, speed: number): number {
  return Math.max(MIN_MESSAGE_WINDOW_MS, messageReadingTimeMs(text) * speed)
}

/** Clamps a cursor into the hunt's extent. */
export function clampCursor(cursor: number, startsAt: number, endsAt: number): number {
  if (cursor < startsAt) return startsAt
  if (cursor > endsAt) return endsAt
  return cursor
}

/**
 * Where pressing play should resume from.
 *
 * At (or past) the end, play restarts the hunt rather than sitting on a finished
 * timeline doing nothing — matching mobile, and the only behaviour that makes the
 * button feel alive after an auto-pause.
 */
export function playbackResumeFrom(cursor: number, startsAt: number, endsAt: number): number {
  return cursor >= endsAt ? startsAt : cursor
}

/**
 * `H:MM:SS` for a hunt's length.
 *
 * The hour is always present, even at zero: dropping it under an hour yields `45:00`,
 * which reads as forty-five seconds just as readily as forty-five minutes.
 */
export function formatReplayDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000))
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${Math.floor(total / 3600)}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`
}

/** A skip step as a short label: `15s`, `30s`, `1m`, `60m`. */
export function formatSkipStep(ms: number): string {
  return ms < 60_000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60_000)}m`
}
